import cx from 'classnames'
import { FC, useCallback, useEffect, useState } from 'react'
import {
  closeTickerRating,
  saveTickerRating
} from '../../../store/admin/tickerRating/actions'
import {
  getTickerRatingApiCall,
  getTickerRatingIsOpen,
  getTickerSuggestions
} from '../../../store/admin/tickerRating/selectors'
import { SAndPRating, RatingsGroups } from '../../../store/filter/types'
import { getAllSecuritiesLoaded } from '../../../store/securities/selectors'
import { useAppDispatch, useAppSelector } from '../../../store/types'
import Loading from '../../Loading/Loading'
import ComponentFooter from '../../ComponentFooter/ComponentFooter'
import ComponentHeader from '../../ComponentHeader/ComponentHeader'
import Modal from '../../GenericModal/Modal'
import GroupedSingleSelect from '../../GroupedSingleSelect/GroupedSingleSelect'
import TickerSearch from '../../TickerSearch/TickerSearch'

import styles from './TickerRating.module.scss'

const TickerRating: FC = () => {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(getTickerRatingIsOpen)
  const securitiesLoaded = useAppSelector(getAllSecuritiesLoaded)
  const apiCall = useAppSelector(getTickerRatingApiCall)
  const suggestions = useAppSelector(getTickerSuggestions)

  const [ticker, setTicker] = useState('')
  const [rating, setRating] = useState<SAndPRating | undefined>(undefined)
  const [showStatus, setShowStatus] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setTicker('')
      setRating(undefined)
      setShowStatus(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (apiCall.status) {
      setShowStatus(true)
      if (!apiCall.error) {
        setTicker('')
        setRating(undefined)
      }
    }
  }, [apiCall.status, apiCall.error])

  const handleClose = useCallback(
    () => dispatch(closeTickerRating()),
    [dispatch]
  )

  const handleSubmit = useCallback(() => {
    if (!ticker.trim() || !rating) return
    dispatch(saveTickerRating({ ticker: ticker.trim(), sAndPRating: rating }))
  }, [dispatch, ticker, rating])

  const handleTickerSelect = useCallback((value: string) => {
    setTicker(value)
  }, [])

  const handleTickerClear = useCallback(() => {
    setTicker('')
    setRating(undefined)
  }, [])

  const handleRatingChange = useCallback(
    (value: string | undefined) => setRating(value as SAndPRating | undefined),
    []
  )

  const matchingSuggestion = suggestions.find(
    (s) => s.ticker === ticker.toUpperCase()
  )

  useEffect(() => {
    setRating(matchingSuggestion?.rating as SAndPRating | undefined)
  }, [matchingSuggestion])
  const canSubmit =
    ticker.trim() !== '' && rating !== undefined && !apiCall.pending

  if (!isOpen) return null

  return (
    <Modal>
      <ComponentHeader title="Update Ticker Rating" showInOpenFin />
      <div className={styles.body}>
        {!securitiesLoaded ? (
          <Loading />
        ) : (
          <>
            <div className={styles.controls}>
              <div>
                <TickerSearch
                  value={ticker}
                  onChange={setTicker}
                  onClear={handleTickerClear}
                  onSelect={handleTickerSelect}
                  suggestions={suggestions}
                  testId="ticker-search"
                />
                <div className={styles.matchCount}>
                  {matchingSuggestion && (
                    <>
                      <strong>
                        {matchingSuggestion.count} bond
                        {matchingSuggestion.count !== 1 && 's'}
                      </strong>{' '}
                      on {matchingSuggestion.ticker}
                    </>
                  )}
                </div>
              </div>
              <GroupedSingleSelect
                groups={RatingsGroups}
                value={rating}
                onChange={handleRatingChange}
                placeholder="Rating"
                testId="rating-select"
              />
            </div>
            <div className={styles.statusArea} aria-live="polite">
              {showStatus && apiCall.status && (
                <div
                  className={cx(styles.statusMsg, {
                    [styles.success]: !apiCall.error,
                    [styles.error]: apiCall.error
                  })}
                >
                  <span>{apiCall.status}</span>
                  <button
                    type="button"
                    className={styles.dismissBtn}
                    onClick={() => setShowStatus(false)}
                    data-testid="dismiss-status"
                    aria-label="Dismiss status"
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <ComponentFooter
        onCancelClick={handleClose}
        onApplyClick={handleSubmit}
        submitText="Submit"
        disableSubmit={!canSubmit}
        submitting={apiCall.pending}
      />
    </Modal>
  )
}

export default TickerRating
